import { Injectable } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import type { Libraries, Library } from '@googlemaps/js-api-loader';

@Injectable({
    providedIn: 'root',
})
export class GoogleMapsLoaderService {
    private loadingPromise: Promise<typeof google> | null = null;
    private isLoaded = false;
    private loadedLibraries = new Set<Library>();

    /**
     * Ładuje Google Maps API jeśli jeszcze nie jest załadowane.
     * Zwraca Promise który resolve'uje się gdy API jest gotowe do użycia.
     * Jeśli API jest już załadowane lub w trakcie ładowania, zwraca odpowiedni Promise.
     */
    async load(apiKey: string, libraries: Libraries = ['marker']): Promise<typeof google> {
        if (this.isLoaded && window.google?.maps) {
            await this.loadMissingLibraries(libraries);
            return Promise.resolve(window.google);
        }

        if (this.loadingPromise) {
            await this.loadingPromise;
            await this.loadMissingLibraries(libraries);
            return window.google;
        }

        this.loadingPromise = this.loadGoogleMapsApi(apiKey, libraries);

        try {
            const google = await this.loadingPromise;
            this.isLoaded = true;
            libraries.forEach((library) => this.loadedLibraries.add(library));
            return google;
        } catch (error) {
            this.loadingPromise = null;
            throw error;
        }
    }

    /**
     * Sprawdza czy Google Maps API jest już załadowane
     */
    isApiLoaded(): boolean {
        return this.isLoaded && !!window.google?.maps;
    }

    private async loadGoogleMapsApi(
        apiKey: string,
        libraries: Libraries
    ): Promise<typeof google> {
        const loader = new Loader({
            apiKey,
            version: 'weekly',
            libraries,
        });

        return loader.load();
    }

    private async loadMissingLibraries(libraries: Libraries): Promise<void> {
        const missingLibraries = libraries.filter((library) => !this.loadedLibraries.has(library));
        if (!missingLibraries.length) {
            return;
        }

        for (const library of missingLibraries) {
            await google.maps.importLibrary(library);
            this.loadedLibraries.add(library);
        }
    }
}
